'use client';

import { useState, useEffect } from 'react'; // 1. Import useEffect
import { MapPin, Users, Heart, Dumbbell, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Color Scheme Definitions ---
const primaryColor = '#faaf40'; // Aura Gold
const secondaryColor = '#d98d4a'; // Darker Gold/Bronze

const reasons = [
  {
    title: 'INVEST IN YOUR HEALTH',
    icon: CreditCard,
    key: 'membership',
    subtitle: 'TRANSPARENT MEMBERSHIP PLANS',
    description:
      'A gym membership gives you access to equipment. A personal trainer gives you guidance for an hour. Aura gives you **both**—plus technology, tracking, accountability, and results—compressed into **20 minutes** that actually fit your life.',
    image: '/aura-membership-plans.jpg',
  },
  {
    title: 'EXPERT TRAINERS',
    icon: Users,
    key: 'trainers',
    subtitle: 'AURA WISDOM',
    description:
      'Our trainers embody **Ancient Wisdom** combined with modern techniques, offering personalized programs for sustained functional strength and **Flexibility**.',
    image: '/aura-expert-trainers.jpg',
  },
  {
    title: 'PREMIUM FACILITIES',
    icon: Heart,
    key: 'facilities',
    subtitle: 'CELLS & ENERGY',
    description:
      'Experience luxury amenities and facilities designed to optimize your **Cells** and maximize your **Energy**. Wellness is a complete experience here.',
    image: '/aura-premium-facilities.jpg',
  },
  {
    title: 'STATE-OF-THE-ART EQUIPMENT',
    icon: Dumbbell,
    key: 'equipment',
    subtitle: 'PURE SCIENCE',
    description:
      "We house the latest and best gym machinery, validated by **Pure Science**, to support every workout style—whether it's strength training, cardio, or functional fitness.",
    image: '/aura-treadmill-window.jpg',
  },
];

export default function WhyChooseAuraInteractive() {
  const [activeReason, setActiveReason] = useState(reasons[0]);

  // ==========================================
  // 2. PIXEL TRACKING LOGIC ADDED HERE
  // ==========================================
  useEffect(() => {
    // Check if Pixel is loaded in the browser
    if (typeof window !== 'undefined' && window.fbq) {
      
      // Track "ViewContent" every time the user clicks a new reason
      window.fbq('track', 'ViewContent', {
        content_name: activeReason.title, // e.g., "EXPERT TRAINERS"
        content_category: 'Why Choose Aura',
        content_ids: [activeReason.key],  // e.g., "trainers"
        content_type: 'slide'
      });
      
    }
  }, [activeReason]); // This runs whenever 'activeReason' changes
  // ==========================================

  // Helper to colorize bold text to Gold instead of White for better contrast
  const renderDescription = (text) =>
    text.split('**').map((part, i) =>
      i % 2 === 1 ? (
        <span key={i} className="text-[#faaf40] font-bold">
          {part}
        </span>
      ) : (
        part
      )
    );

  return (
    <section className="py-12 md:py-24 font-sans overflow-hidden relative bg-black text-white">
      
      {/* --- BACKGROUND GRADIENT (FIXED) --- */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-black via-[#1a1a1a] to-black"
        style={{
          backgroundImage: `
            radial-gradient(circle at 0% 0%, #2a2a2a 0%, transparent 50%), 
            radial-gradient(circle at 100% 100%, ${secondaryColor}33 0%, transparent 40%)
          `
        }}
      ></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 relative">
        
        {/* Header */}
        <div className="flex justify-start lg:justify-end mb-8 md:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-tight drop-shadow-xl">
            <span className="text-white">WHY CHOOSE</span>{' '}
            <span className="text-[#faaf40] block md:inline drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              AURA WELLNESS?
            </span>
          </h2>
        </div>

        {/* Main Layout */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-0 items-start">

          {/* LEFT: Image + Dynamic Box */}
          <div className="w-full lg:w-1/2 relative h-[400px] sm:h-[500px] lg:h-[600px] lg:min-h-[550px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeReason.key + '-image'}
                className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src={activeReason.image}
                  alt={activeReason.title}
                  className="object-cover w-full h-full"
                />
                <div className="absolute inset-0 bg-black/30" />
              </motion.div>
            </AnimatePresence>

            {/* Dynamic Info Box */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeReason.key + '-text'}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="absolute bottom-0 left-0 z-20 w-full p-6 sm:p-8 lg:p-10 
                           bg-black/60 backdrop-blur-xl border-t border-r border-white/10
                           rounded-bl-2xl lg:rounded-tr-2xl lg:max-w-md shadow-2xl"
              >
                <div
                  className="text-left mb-3 py-1 px-3 inline-block rounded border border-[#faaf40]/50 bg-black/40 backdrop-blur-md"
                >
                  <span className="text-[#faaf40] font-bold text-xs tracking-widest uppercase">
                    {activeReason.subtitle}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold uppercase mb-2 text-white drop-shadow-md">
                  {activeReason.title}
                </h3>

                <p className="text-sm sm:text-base text-gray-200 leading-relaxed font-medium">
                  {renderDescription(activeReason.description)}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* RIGHT: Interactive Reason List */}
          <div className="w-full lg:w-1/2 pt-4 lg:pt-0 pl-0 lg:pl-16 flex flex-col justify-center">
            {reasons.map((reason) => {
              const isSelected = activeReason.key === reason.key;
              return (
                <div
                  key={reason.key}
                  className="py-4 cursor-pointer group"
                  onClick={() => setActiveReason(reason)}
                >
                  <div className="flex items-center gap-4">
                    {/* Icon Circle */}
                    <div
                      className={`p-2 rounded-full transition-colors duration-300 ${
                        isSelected 
                          ? 'bg-[#faaf40] text-black shadow-[0_0_15px_rgba(250,175,64,0.4)]'
                          : 'bg-white/10 text-gray-400 group-hover:bg-white/20 group-hover:text-white'
                      }`}
                    >
                      <reason.icon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                    </div>

                    {/* Title */}
                    <h4
                      className={`text-lg sm:text-xl font-bold uppercase transition-colors duration-200 ${
                        isSelected
                          ? 'text-[#faaf40]'
                          : 'text-gray-500 group-hover:text-white'
                      }`}
                    >
                      {reason.title}
                    </h4>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3 relative h-0.5 sm:h-1 w-full bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out ${
                        isSelected ? 'w-full opacity-100' : 'w-0 opacity-0'
                      }`}
                      style={{ 
                        background: primaryColor,
                        boxShadow: '0 0 10px rgba(250,175,64,0.8)' 
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}